import * as THREE from 'three';
import { Random } from '../core/Random';
import { possum, satelliteDish } from './builders/decor';
import {
  burnBarrel,
  burnedShell,
  cautionTape,
  cornholeBag,
  cornholeBoard,
  creekWater,
  deadTree,
  flickerLamp,
  graffiti,
  moonshineStill,
  outhouse,
  picnicTable,
  plankBridge,
  reeds,
  schoolBus,
  shoppingCart,
  tireMountain,
  utilityPedestal,
} from './builders/grunge';
import {
  bush,
  chainLinkFence,
  couch,
  crushedCar,
  firePit,
  fridge,
  lawnChair,
  mattress,
  oilDrumSmoker,
  pennants,
  powerPole,
  recliner,
  rockingChair,
  signBoard,
  tireStack,
  waterTower,
  wire,
} from './builders/props';
import { buildTrailer, type TrailerSpec } from './builders/trailer';
import { buildBoatOnTrailer, buildCar } from './builders/vehicles';
import type { CollisionWorld } from './Collision';
import { HOME_LOTS, homeSpots, POSSUM_TROT_X } from './home';
import {
  BACK_FORTY,
  CRICK,
  crickZ,
  DECK_DEPTH,
  SPOTS,
  TRAILER_LENGTH,
  TRAILER_WIDTH,
  type Vec2,
} from './layout';
import { box, M, mat, mesh } from './materials';

type Animator = (time: number, dt: number, night: number) => void;

/** The bits of World a district builder uses. */
export interface WorldKit {
  readonly root: THREE.Group;
  readonly dynamic: THREE.Group;
  readonly collision: CollisionWorld;
  place<T extends THREE.Object3D>(obj: T, x: number, z: number, rotY?: number, parent?: THREE.Object3D): T;
  solid(cx: number, cz: number, w: number, d: number, h: number, quarterTurns?: number): void;
  animate(fn: Animator): void;
  addSmoke(x: number, y: number, z: number): void;
}

const forRentSign = (): THREE.Group =>
  signBoard(
    {
      w: 512,
      h: 256,
      bg: '#ffffff',
      fg: '#c62828',
      border: '#c62828',
      borderWidth: 12,
      lines: [
        { text: 'LOT FOR RENT', size: 80, font: 'Anton' },
        { text: 'HOOKUPS INCLUDED • SEE DARLENE', size: 34, font: 'Anton', color: '#111' },
      ],
      grime: true,
    },
    1.6,
    0.8,
    0.9,
  );

/** A trailer nobody has lived in for years. */
function derelict(seed: number, color: string, side: number): TrailerSpec {
  const flip = -side;
  return {
    color,
    stripe: '#5a4a3a',
    rusty: true,
    tarp: seed % 2 === 0,
    windows: [
      { x: -3.4 * flip, side: 1, kind: 'boarded' },
      { x: 0.8 * flip, side: 1, kind: seed % 3 === 0 ? 'cardboard' : 'boarded' },
      { x: -2 * flip, side: -1, kind: 'boarded' },
      { x: 2.6 * flip, side: -1, kind: 'cardboard' },
    ],
    skirting: 'none',
    steps: 'blocks',
    seed,
    doorX: -side * 4,
    hitch: -side,
  };
}

/** Graffiti on the street-facing (+z) wall of a trailer at (cx, cz). */
function tag(kit: WorldKit, text: string, color: string, x: number, cz: number, y = 1.6): void {
  const g = graffiti(text, color, 2.6, 1.1);
  g.position.set(x, y, cz + TRAILER_WIDTH / 2 + 0.06);
  kit.root.add(g);
}

/**
 * Possum Trot Lane: a second street of lots west of Main Street, where neighbors move
 * in when they join your park. It gets rougher the farther north you go.
 */
export function buildPossumTrot(kit: WorldKit, rng: Random): void {
  const x0 = POSSUM_TROT_X;
  // Power poles down the east edge, at the lot lines.
  const anchors: THREE.Vector3[][] = [];
  for (const z of [38, 22, 6, -10, -26, -42]) {
    const p = powerPole(z === 6);
    kit.place(p.group, x0 + 4.8, z);
    kit.collision.addCircle(x0 + 4.8, z, 0.25);
    anchors.push(p.anchors.map((a) => new THREE.Vector3(x0 + 4.8 + a.x, a.y, z + a.z)));
  }
  for (let i = 0; i < anchors.length - 1; i++)
    for (let k = 0; k < 2; k++) kit.root.add(wire(anchors[i]![k]!, anchors[i + 1]![k]!, 0.9));

  const street = signBoard(
    {
      w: 512,
      h: 128,
      bg: '#1f6b3a',
      fg: '#ffffff',
      border: '#ffffff',
      borderWidth: 6,
      lines: [{ text: 'POSSUM TROT LN', size: 72, font: 'Anton' }],
    },
    1.4,
    0.35,
    2.4,
    1,
  );
  kit.place(street, SPOTS.possumTrotSign.x, SPOTS.possumTrotSign.z);
  kit.collision.addCircle(SPOTS.possumTrotSign.x, SPOTS.possumTrotSign.z - 0.1, 0.12);

  // Row -34: a condemned trailer (good copper in the walls) and a lot gone to seed.
  const c = SPOTS.condemned;
  kit.place(buildTrailer(derelict(24, '#c9c2ae', 1)), c.x, c.z);
  kit.solid(c.x, c.z, TRAILER_LENGTH, TRAILER_WIDTH, 3.4);
  kit.collision.addRect(SPOTS.condemnedDoor.x, SPOTS.condemnedDoor.z - 0.8, 1.0, 0.9);
  tag(kit, 'KEEP OUT', '#d62828', c.x + 2, c.z);
  const notice = signBoard(
    {
      w: 384,
      h: 256,
      bg: '#fff7d6',
      fg: '#b31d1d',
      border: '#b31d1d',
      borderWidth: 10,
      lines: [
        { text: 'CONDEMNED', size: 76, font: 'Anton' },
        { text: 'UNFIT FOR HUMAN HABITATION', size: 26, font: 'Anton', color: '#111' },
        { text: '(DALE LIVED HERE 2 YRS)', size: 24, font: 'Anton', color: '#555' },
      ],
      grime: true,
    },
    0.9,
    0.6,
    0.9,
    1,
  );
  kit.place(notice, c.x - 6.8, c.z + 4.4, -Math.PI / 2 + 0.3);
  kit.collision.addCircle(c.x - 6.8, c.z + 4.4, 0.12);
  for (const [a, b] of [
    [new THREE.Vector3(c.x - 7, 0.9, c.z + 3.2), new THREE.Vector3(c.x - 2, 0.9, c.z + 6)],
    [new THREE.Vector3(c.x - 2, 0.9, c.z + 6), new THREE.Vector3(c.x + 4, 0.9, c.z + 5.2)],
  ] as const)
    kit.root.add(cautionTape(a, b));
  kit.place(tireStack(rng, 4), c.x + 7.5, c.z + 3.5);
  kit.collision.addCircle(c.x + 7.5, c.z + 3.5, 0.5);

  const west = { x: x0 - 16.5, z: -38.5 };
  const junker = buildCar({ kind: 'sedan', color: '#5a6a3a', onBlocks: true, rusty: true });
  kit.place(junker.group, west.x + 7, west.z + 6, 0.3);
  kit.collision.addRect(west.x + 7, west.z + 6, 4.8, 2.2);
  // Meemaw Pearl's place: the tidiest trailer in the county, and she'll tell you so.
  kit.place(
    buildTrailer({
      color: '#f3d9e6',
      stripe: '#a8346b',
      trim: '#ffffff',
      doorX: 4,
      windows: [
        { x: -3.4, side: 1, kind: 'blinds' },
        { x: 0.6, side: 1, kind: 'glass' },
        { x: -2, side: -1, kind: 'blinds' },
      ],
      skirting: 'lattice',
      steps: 'deck',
      awning: true,
      porchLight: true,
      seed: 61,
      hitch: 1,
    }),
    west.x,
    west.z,
  );
  kit.solid(west.x, west.z, TRAILER_LENGTH, TRAILER_WIDTH, 3.4);
  const door = SPOTS.meemawDoor;
  kit.collision.addRect(door.x, door.z + DECK_DEPTH / 2, 3.5, DECK_DEPTH);
  const m = SPOTS.meemaw;
  kit.place(rockingChair(), m.x, m.z - 0.05, Math.PI / 2, kit.dynamic);
  for (const dx of [-3, -1.6, 6.5, 7.6]) {
    const pot = new THREE.Group();
    pot.add(box(0.4, 0.35, 0.4, mat('#b5622d'), 0, 0.17, 0));
    pot.add(
      new THREE.Mesh(
        new THREE.SphereGeometry(0.28, 6, 4),
        mat(rng.pick(['#e63946', '#ffd166', '#f78fb3', '#c77dff'])),
      ).translateY(0.5),
    );
    kit.place(pot, west.x + dx, west.z + 2.6);
  }
  for (let i = 0; i < 4; i++) kit.place(bush(rng), west.x + rng.range(-8, -2), west.z + rng.range(3, 6));
}

/** Vacant-lot dressing for an empty home lot. Hidden while someone lives there. */
export interface VacantLot {
  group: THREE.Group;
  shapes: Array<{ x: number; z: number; r: number }>;
}

export function buildVacantHome(index: number): VacantLot {
  const frame = HOME_LOTS[index];
  if (!frame) throw new Error(`no home lot ${index}`);
  const h = homeSpots(frame);
  const rng = new Random(500 + index);
  const g = new THREE.Group();
  const shapes: VacantLot['shapes'] = [];
  const at = (obj: THREE.Object3D, p: Vec2, rot = 0, r = 0): void => {
    obj.position.set(p.x, obj.position.y, p.z);
    obj.rotation.y = rot;
    g.add(obj);
    if (r > 0) shapes.push({ x: p.x, z: p.z, r });
  };
  const side = frame.side;
  // The sign faces the street.
  at(forRentSign(), { x: h.mailbox.x - side * 0.6, z: h.mailbox.z + 1.4 }, -side * (Math.PI / 2 - 0.25), 0.2);
  at(utilityPedestal(), { x: h.trailer.x - side * 5, z: h.trailer.z + 3.2 }, 0, 0.25);
  // A concrete pad where the trailer would go.
  const pad = mesh(new THREE.PlaneGeometry(12.5, 4.5), mat('#9a958a', { rough: 1 }), 0, 0.02, 0, {
    cast: false,
  });
  pad.rotation.x = -Math.PI / 2;
  at(pad, h.trailer);
  // Weeds that nobody mows (it's not anybody's lot... yet).
  const blade = new THREE.ConeGeometry(0.09, 0.7, 3);
  blade.translate(0, 0.35, 0);
  const count = 90;
  const weeds = new THREE.InstancedMesh(blade, mat('#7d8f4a', { flat: true }), count);
  const m4 = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const area = h.area;
  for (let i = 0; i < count; i++) {
    const x = rng.range(area.minX + 1, area.maxX - 1);
    const z = rng.range(area.minZ + 1, area.maxZ - 1);
    if (Math.abs(z - h.trailer.z) < 3 && Math.abs(x - h.trailer.x) < 7) continue;
    const s = rng.range(0.4, 0.85);
    q.setFromEuler(new THREE.Euler(rng.range(-0.3, 0.3), rng.range(0, 6), rng.range(-0.3, 0.3)));
    m4.compose(new THREE.Vector3(x, 0, z), q, new THREE.Vector3(s, s * rng.range(0.6, 1.2), s));
    weeds.setMatrixAt(i, m4);
  }
  weeds.receiveShadow = true;
  g.add(weeds);
  if (index > 0 && rng.chance(0.6)) at(tireStack(rng, rng.int(2, 4)), h.decor.pool!, 0, 0.45);
  if (index > 0 && rng.chance(0.4)) {
    const cart = shoppingCart();
    cart.rotation.z = 1.4;
    at(cart, h.decor.trampoline!, rng.range(0, 3));
  }
  return { group: g, shapes };
}

/** Possum Trot Commons: cornhole, a picnic table and a fire pit at the south end of the lane. */
export function buildCommons(kit: WorldKit, rng: Random): void {
  const a = SPOTS.cornholeA;
  const b = SPOTS.cornholeB;
  // Boards face each other: low front edges toward the other board.
  kit.place(cornholeBoard('#c62828'), a.x, a.z, Math.PI);
  kit.place(cornholeBoard('#1d4e9e'), b.x, b.z, 0);
  kit.collision.addRect(a.x, a.z, 1.3, 0.7);
  kit.collision.addRect(b.x, b.z, 1.3, 0.7);
  for (let i = 0; i < 4; i++) {
    const bag = cornholeBag(i % 2 ? '#c62828' : '#1d4e9e');
    bag.position.set(b.x + rng.range(-0.5, 0.6), 0.05 + (i < 2 ? 0.25 : 0), b.z + rng.range(-0.25, 0.25));
    bag.rotation.y = rng.range(0, 3);
    kit.dynamic.add(bag);
  }
  kit.place(picnicTable(), SPOTS.picnicTable.x, SPOTS.picnicTable.z);
  kit.collision.addRect(SPOTS.picnicTable.x, SPOTS.picnicTable.z, 2.3, 2);
  const pit = SPOTS.commonsFirePit;
  kit.place(firePit(), pit.x, pit.z);
  kit.collision.addCircle(pit.x, pit.z, 0.75);
  ['#2f8f3a', '#e0b000', '#c0392b'].forEach((color, i) => {
    const ang = (i / 3) * Math.PI * 2 + 0.6;
    kit.place(lawnChair(color), pit.x + Math.cos(ang) * 1.8, pit.z + Math.sin(ang) * 1.8, -ang - Math.PI / 2);
  });
  kit.place(oilDrumSmoker(), pit.x + 4.5, pit.z + 4, 0.4);
  kit.collision.addRect(pit.x + 4.5, pit.z + 4, 1.3, 0.8);
  const posts = [
    new THREE.Vector3(-84, 3.4, 42),
    new THREE.Vector3(-46, 3.4, 42),
    new THREE.Vector3(-46, 3.4, 51.5),
    new THREE.Vector3(-84, 3.4, 51.5),
  ];
  for (const p of posts) {
    kit.root.add(box(0.12, 3.4, 0.12, M.pole, p.x, 1.7, p.z));
    kit.collision.addCircle(p.x, p.z, 0.12);
  }
  const cols = ['#c62828', '#ffffff', '#1d4e9e', '#ffd400'];
  kit.root.add(pennants(posts[0]!, posts[1]!, cols), pennants(posts[3]!, posts[2]!, cols));
  const sign = signBoard(
    {
      w: 768,
      h: 256,
      bg: '#f3e6c4',
      fg: '#3a2412',
      border: '#3a2412',
      borderWidth: 12,
      gap: 6,
      lines: [
        { text: 'POSSUM TROT COMMONS', size: 72, font: 'Rye', color: '#2f6b3a' },
        { text: 'NO LIFEGUARD ON DUTY • ALSO NO POOL', size: 34, font: 'Anton' },
      ],
      grime: true,
    },
    2.4,
    0.8,
    1.1,
  );
  kit.place(sign, -64, 52.2, Math.PI);
  kit.collision.addRect(-64, 52.2, 2.4, 0.3);
}

export interface BackFortyProps {
  /** The still's coals: a stage turns them up when you stoke the fire. */
  stillFire: THREE.MeshStandardMaterial;
  /** Set while a stage drives the fire. */
  stillBusy: boolean;
}

/** Lot 13 and the rest of the Back Forty: the side of the park the brochure leaves out. */
export function buildBackForty(kit: WorldKit, rng: Random): BackFortyProps {
  const x0 = POSSUM_TROT_X;

  // --- the entrance ---------------------------------------------------------------
  const s = SPOTS.backFortySign;
  const sign = signBoard(
    {
      w: 768,
      h: 384,
      bg: '#d8ccaa',
      fg: '#3a2412',
      border: '#3a2412',
      borderWidth: 14,
      gap: 8,
      lines: [
        { text: 'LUCKY ACRES', size: 96, font: 'Rye', color: '#4f5f3a' },
        { text: 'THE BACK FORTY', size: 60, font: 'Rye' },
        { text: 'NO TRESPASSING • NO REFUNDS', size: 40, font: 'Anton', color: '#7a1f2b' },
      ],
      grime: true,
    },
    2.8,
    1.4,
    1.0,
  );
  kit.place(sign, s.x, s.z);
  kit.collision.addRect(s.x, s.z, 2.9, 0.3);
  // Somebody fixed the sign.
  const un = graffiti('UN', '#e0201b', 0.9, 0.6, 200);
  un.position.set(s.x - 1.22, 2.18, s.z + 0.09);
  un.rotation.z = 0.18;
  kit.root.add(un);
  const lamp = flickerLamp();
  // Across the road, so it doesn't sprout out of the sign.
  kit.place(lamp.group, x0 - 4.6, -57, Math.PI / 2);
  kit.collision.addCircle(x0 - 4.6, -57, 0.2);
  kit.animate((t, _dt, night) => {
    const buzz = Math.sin(t * 37) > 0.2 && Math.sin(t * 3.1 + Math.sin(t * 0.7) * 4) > -0.6;
    lamp.bulb.emissiveIntensity = night > 0.3 ? (buzz ? 1.6 * night : 0.05) : 0;
  });

  // --- Lot 13 -----------------------------------------------------------------------
  const l13 = SPOTS.lot13;
  kit.place(burnedShell(rng), l13.x, l13.z);
  kit.solid(l13.x, l13.z, 12, 4, 2.4);
  const scorch = mesh(new THREE.CircleGeometry(8, 18), mat('#1f1a16', { rough: 1 }), 0, 0.03, 0, {
    cast: false,
  });
  scorch.rotation.x = -Math.PI / 2;
  scorch.scale.set(1, 0.6, 1);
  kit.place(scorch, l13.x, l13.z + 1);
  const block = box(0.45, 0.25, 0.4, M.cinder, 0, 0.12, 0);
  kit.place(block, l13.x - 7.6, l13.z + 4.6);
  const thirteen = graffiti('13', '#e0201b', 0.5, 0.3, 200);
  thirteen.position.set(l13.x - 7.6, 0.13, l13.z + 4.81);
  kit.root.add(thirteen);
  const chair = rockingChair();
  kit.place(chair, l13.x - 5.5, l13.z + 5.5, -Math.PI / 2 - 0.4);
  kit.collision.addRect(l13.x - 5.5, l13.z + 5.5, 0.8, 0.8);
  kit.root.add(
    cautionTape(
      new THREE.Vector3(l13.x - 8, 0.95, l13.z + 3),
      new THREE.Vector3(l13.x + 6, 0.95, l13.z + 3.6),
    ),
  );

  // --- abandoned trailers -------------------------------------------------------------
  const aa = SPOTS.abandonedA;
  kit.place(buildTrailer(derelict(31, '#b8b09a', -1)), aa.x, aa.z);
  kit.solid(aa.x, aa.z, TRAILER_LENGTH, TRAILER_WIDTH, 3.4);
  kit.collision.addRect(SPOTS.abandonedADoor.x, SPOTS.abandonedADoor.z - 0.6, 1.0, 0.9);
  tag(kit, 'LOT 14½', '#2b2b2b', aa.x - 2.5, aa.z);
  kit.place(couch('#6b5b3a'), aa.x + 1, aa.z + 4.5, 0.2);
  kit.collision.addRect(aa.x + 1, aa.z + 4.5, 2.1, 1);
  kit.place(tireMountain(rng, 9, 1.2), aa.x - 8, aa.z + 4);
  kit.collision.addCircle(aa.x - 8, aa.z + 4, 1.3);

  const ab = SPOTS.abandonedB;
  const leaning = buildTrailer(derelict(32, '#a9b4a8', 1));
  leaning.rotation.z = 0.05;
  kit.place(leaning, ab.x, ab.z);
  kit.solid(ab.x, ab.z, TRAILER_LENGTH, TRAILER_WIDTH, 3.4);
  kit.collision.addRect(SPOTS.abandonedBDoor.x, SPOTS.abandonedBDoor.z - 0.6, 1.0, 0.9);
  tag(kit, 'PETE WUZ HERE', '#2b6cb0', ab.x + 1.5, ab.z);
  kit.place(buildBoatOnTrailer('#b9c4c9'), ab.x + 9, ab.z + 6, 0.5);
  kit.collision.addRect(ab.x + 9, ab.z + 6, 4.6, 3);

  // --- burn barrel camp under the water tower -------------------------------------------
  const camp = SPOTS.barrelCamp;
  const tower = waterTower('LUCKY ACRES');
  // The lettering is centered on the tank's local -z side: turn it toward the Back Forty gate.
  const toGate = Math.atan2(
    -(SPOTS.backFortySign.x - SPOTS.waterTower.x),
    -(SPOTS.backFortySign.z - SPOTS.waterTower.z),
  );
  kit.place(tower, SPOTS.waterTower.x, SPOTS.waterTower.z, toGate);
  kit.collision.addCircle(SPOTS.waterTower.x, SPOTS.waterTower.z, 3);
  const flames: THREE.MeshStandardMaterial[] = [];
  for (const [dx, dz] of [
    [0, 0],
    [3.2, 1.6],
    [-2.6, 2.4],
  ] as const) {
    const barrel = burnBarrel();
    kit.place(barrel.group, camp.x + dx, camp.z + dz);
    kit.collision.addCircle(camp.x + dx, camp.z + dz, 0.4);
    kit.addSmoke(camp.x + dx, 1.3, camp.z + dz);
    flames.push(barrel.flame);
  }
  kit.animate((t) => {
    flames.forEach((f, i) => {
      f.emissiveIntensity = 1.5 + Math.sin(t * 11 + i * 2) * 0.35 + Math.sin(t * 23 + i) * 0.2;
    });
  });
  kit.place(couch('#4a5a3a'), camp.x - 1, camp.z - 3, Math.PI);
  kit.collision.addRect(camp.x - 1, camp.z - 3, 2.1, 1);
  kit.place(recliner('#6b4a33'), camp.x + 3.5, camp.z - 2.5, Math.PI + 0.5);
  kit.collision.addRect(camp.x + 3.5, camp.z - 2.5, 1, 1);
  kit.place(fridge('#d8c9a3'), camp.x - 5, camp.z - 1.5, 0.8);
  kit.collision.addCircle(camp.x - 5, camp.z - 1.5, 0.5);
  const tarp = box(4, 0.05, 3, mat('#5f6b3a', { rough: 0.7 }), 0, 2.2, 0);
  tarp.rotation.x = 0.25;
  kit.place(tarp, camp.x - 0.5, camp.z - 3.4);
  for (const dx of [-2, 1.5]) {
    kit.root.add(box(0.1, 2.3, 0.1, M.pole, camp.x - 0.5 + dx, 1.15, camp.z - 2.2));
    kit.collision.addCircle(camp.x - 0.5 + dx, camp.z - 2.2, 0.1);
  }

  // --- the crick ------------------------------------------------------------------------
  kit.root.add(creekWater(CRICK.minX, CRICK.maxX, CRICK.width, crickZ));
  const bridgeZ = crickZ(x0);
  kit.place(plankBridge(CRICK.width + 3.5, 6.4), x0, bridgeZ);
  // The water is waist-deep muck: walk around it, or over the bridge.
  for (let x = CRICK.minX; x < CRICK.maxX; x += 3) {
    const cx = x + 1.5;
    if (Math.abs(cx - x0) < 4.8) continue;
    kit.collision.addRect(cx, crickZ(cx), 3.1, CRICK.width + 0.4);
  }
  for (const rail of [-1, 1]) kit.collision.addRect(x0 + rail * 3.3, bridgeZ, 0.3, CRICK.width + 3);
  const reedPoints: THREE.Vector3[] = [];
  for (let i = 0; i < 260; i++) {
    const x = rng.range(CRICK.minX + 2, CRICK.maxX - 2);
    if (Math.abs(x - x0) < 5) continue;
    const bank = rng.chance(0.5) ? 1 : -1;
    reedPoints.push(new THREE.Vector3(x, 0, crickZ(x) + bank * (CRICK.width / 2 + rng.range(-0.3, 0.9))));
  }
  kit.root.add(reeds(rng, reedPoints));
  for (const x of [-92, -71, -38]) {
    const t = mesh(new THREE.TorusGeometry(0.34, 0.13, 6, 12), M.rubber, 0, 0.12, 0);
    t.rotation.x = Math.PI / 2 - 0.4;
    kit.place(t, x, crickZ(x) + rng.range(-0.8, 0.8));
  }
  const sunkCart = shoppingCart();
  sunkCart.rotation.z = 1.2;
  sunkCart.position.y = -0.3;
  kit.place(sunkCart, -56, crickZ(-56) + 0.6, 0.8);
  // Catfish holes: dark muddy hollows under the bank.
  for (const spot of [SPOTS.noodleA, SPOTS.noodleB, SPOTS.noodleC]) {
    const toward = Math.sign(crickZ(spot.x) - spot.z);
    const hole = mesh(new THREE.CircleGeometry(0.55, 10), mat('#1d1712', { rough: 1 }), 0, 0.05, 0, {
      cast: false,
    });
    hole.rotation.x = -Math.PI / 2;
    hole.scale.set(1.3, 0.8, 1);
    kit.place(hole, spot.x, spot.z + toward * 1.6);
    const stake = box(0.06, 0.9, 0.06, M.trunk, 0, 0.45, 0);
    kit.place(stake, spot.x + 0.8, spot.z + toward * 0.6);
    const rag = box(0.25, 0.12, 0.02, mat('#d62828'), 0, 0.85, 0.04);
    kit.place(rag, spot.x + 0.8, spot.z + toward * 0.6);
  }

  // --- Possum Pete's place (north bank) -----------------------------------------------------
  const bus = SPOTS.peteBus;
  kit.place(schoolBus(rng), bus.x, bus.z);
  kit.solid(bus.x + 0.4, bus.z, 10.4, 2.6, 3);
  const dish = satelliteDish();
  dish.position.y = 2.95;
  kit.place(dish, bus.x - 3.6, bus.z + 0.3);
  kit.place(outhouse(), bus.x + 9, bus.z - 2, -Math.PI / 2);
  kit.solid(bus.x + 9, bus.z - 2, 1.3, 1.3, 2.3);
  const pp = SPOTS.pete;
  kit.place(firePit(), pp.x - 1.5, pp.z + 2.5);
  kit.collision.addCircle(pp.x - 1.5, pp.z + 2.5, 0.75);
  kit.addSmoke(pp.x - 1.5, 0.6, pp.z + 2.5);
  kit.place(lawnChair('#8a6a3a'), pp.x - 3.4, pp.z + 2, 1.2);
  const kevinsCousin = possum();
  kit.place(kevinsCousin.group, bus.x - 2.8, bus.z + 1.8, 0.4, kit.dynamic);

  // --- junk pile and the still ------------------------------------------------------------
  const junk = SPOTS.junkPile;
  for (let i = 0; i < 3; i++) {
    const car = crushedCar(rng.pick(['#7a1f2b', '#2e4f8a', '#6b7a3a', '#3a3f45']), rng);
    kit.place(car, junk.x + rng.range(-0.3, 0.3), junk.z);
    car.position.y = i * 0.95;
  }
  kit.collision.addRect(junk.x, junk.z, 4.2, 2.2);
  kit.place(tireMountain(rng, 14, 1.6), junk.x + 6, junk.z + 1);
  kit.collision.addCircle(junk.x + 6, junk.z + 1, 1.6);
  for (let i = 0; i < 3; i++) {
    const f = fridge(rng.pick(['#f0ede4', '#d8c9a3', '#9aa39a']));
    const x = junk.x - 5 - i * 1.3;
    const z = junk.z + rng.range(-1, 1);
    kit.place(f, x, z, rng.range(0, 6));
    kit.collision.addCircle(x, z, 0.5);
  }
  const mat2 = mattress();
  mat2.rotation.z = 1.25;
  kit.place(mat2, junk.x + 2, junk.z + 4, 0.5);

  const st = SPOTS.still;
  const still = moonshineStill(rng);
  kit.place(still.group, st.x, st.z, 0.3);
  kit.collision.addCircle(st.x, st.z, 0.85);
  kit.collision.addCircle(st.x + 1.65, st.z - 0.5, 0.45);
  kit.collision.addCircle(st.x + 2.75, st.z - 0.75, 0.5);
  kit.addSmoke(st.x, 1.9, st.z);
  const props: BackFortyProps = { stillFire: still.fire, stillBusy: false };
  kit.animate((t) => {
    if (!props.stillBusy) still.fire.emissiveIntensity = 1.2 + Math.sin(t * 9) * 0.3 + Math.sin(t * 21) * 0.2;
  });

  // --- dead trees, a sagging fence, junk everywhere --------------------------------------------
  const busy: Array<Vec2 & { r: number }> = [
    { ...SPOTS.lot13, r: 9 },
    { ...SPOTS.abandonedA, r: 10 },
    { ...SPOTS.abandonedB, r: 12 },
    { ...camp, r: 8 },
    { ...SPOTS.waterTower, r: 5 },
    { ...bus, r: 9 },
    { ...junk, r: 9 },
    { ...st, r: 6 },
    { ...SPOTS.backFortySign, r: 4 },
  ];
  let trees = 0;
  for (let i = 0; i < 400 && trees < 34; i++) {
    const x = rng.range(BACK_FORTY.minX + 2, BACK_FORTY.maxX - 2);
    const z = rng.range(BACK_FORTY.minZ + 2, BACK_FORTY.maxZ - 2);
    if (Math.abs(x - x0) < 6 || Math.abs(z - crickZ(x)) < 4.5) continue;
    if (busy.some((b) => Math.hypot(x - b.x, z - b.z) < b.r)) continue;
    // Keep a footpath clear from the bridge to the still.
    if (z < crickZ(x) - 4 && z > crickZ(x) - 13 && x < x0) continue;
    kit.place(deadTree(rng), x, z, rng.range(0, 6));
    kit.collision.addCircle(x, z, 0.3);
    trees++;
  }
  const fenceX = BACK_FORTY.maxX + 1;
  for (const [z1, z2] of [
    [-56, -72],
    [-78, -88],
    [-97, -110],
  ] as const) {
    const f = chainLinkFence(Math.abs(z2 - z1), 2);
    f.rotation.z = rng.range(-0.06, 0.06);
    kit.place(f, fenceX, (z1 + z2) / 2, Math.PI / 2);
    kit.collision.addRect(fenceX, (z1 + z2) / 2, 0.3, Math.abs(z2 - z1));
  }
  for (let i = 0; i < 14; i++) {
    const x = rng.range(-100, -34);
    const z = rng.range(-110, -58);
    if (busy.some((b) => Math.hypot(x - b.x, z - b.z) < b.r) || Math.abs(x - x0) < 5) continue;
    const puddle = mesh(
      new THREE.CircleGeometry(rng.range(0.8, 2), 12),
      mat('#2a2219', { rough: 0.2 }),
      0,
      0.035,
      0,
      {
        cast: false,
      },
    );
    puddle.rotation.x = -Math.PI / 2;
    puddle.scale.set(1, rng.range(0.5, 1), 1);
    kit.place(puddle, x, z);
  }
  return props;
}
